import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, User2, Hotel, QrCode, CalendarDays, Wallet } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../utils/translations';
import { useCanCollectPayments } from '../hooks/useCanCollectPayments';

const ModernBottomNav = () => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const { language } = useLanguage();
  const { currentUser } = useAuth();
  const { canCollect } = useCanCollectPayments();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isMobile) {
    return null;
  }

  const navItems = [
    { path: '/', labelKey: 'bottomNav.home', logoIcon: true },
    { path: '/category/hotels', labelKey: 'bottomNav.hotels', icon: Hotel },
    { path: '/bookings', labelKey: 'header.bookings', icon: CalendarDays, special: true },
    canCollect
      ? { path: '/ecosystem/encaisser', labelKey: 'bottomNav.collect', icon: QrCode }
      : { path: '/ecosystem/portefeuille', labelKey: 'bottomNav.wallet', icon: Wallet },
    currentUser
      ? { path: '/profile', labelKey: 'bottomNav.profile', icon: User2 }
      : { path: '/search', labelKey: 'bottomNav.search', icon: Search },
  ];

  const activeColor = '#0c3d72';
  const mutedColor = '#6b7280';

  const renderNavIcon = (item, size, isActive) => {
    if (item.logoIcon) {
      return (
        <img
          src="/logo.png"
          alt={t(language, 'header.logoAlt')}
          style={{
            width: size,
            height: size,
            objectFit: 'contain',
            borderRadius: '6px',
            opacity: isActive ? 1 : 0.85
          }}
        />
      );
    }
    if (item.flagIcon) {
      return (
        <img
          src="/guinee.png"
          alt={t(language, 'header.flagAlt')}
          style={{
            width: size,
            height: size,
            objectFit: 'cover',
            borderRadius: '4px',
            opacity: isActive ? 1 : 0.85
          }}
        />
      );
    }

    const Icon = item.icon;
    return (
      <Icon
        size={item.special ? 22 : size}
        color={item.special ? '#ffffff' : (isActive ? activeColor : mutedColor)}
      />
    );
  };

  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255,255,255,0.94)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderTop: '1px solid rgba(12, 61, 114, 0.08)',
        padding: '0.5rem 0 calc(0.5rem + env(safe-area-inset-bottom, 0px))',
        zIndex: 1000,
        boxShadow: '0 -12px 28px rgba(11, 28, 23, 0.08)',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center'
      }}
    >
      {navItems.map((item) => {
        const isActive = location.pathname === item.path;

        return (
          <Link
            key={item.path}
            to={item.path}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.2rem',
              textDecoration: 'none',
              color: isActive ? activeColor : mutedColor,
              padding: item.special ? '0.78rem' : '0.55rem 0.45rem',
              minWidth: item.special ? '52px' : '64px',
              minHeight: item.special ? '52px' : '52px',
              borderRadius: item.special ? '50%' : '14px',
              background: item.special
                ? 'rgba(12, 61, 114, 0.86)'
                : (isActive ? 'rgba(12, 61, 114, 0.08)' : 'transparent'),
              width: item.special ? '52px' : 'auto',
              height: item.special ? '52px' : 'auto',
              marginTop: item.special ? '-20px' : '0',
              boxShadow: item.special ? '0 12px 24px rgba(12, 61, 114, 0.24)' : 'none',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
            aria-label={t(language, item.labelKey)}
          >
            {renderNavIcon(item, 20, isActive)}
            {!item.special && (
              <span
                style={{
                  fontSize: '0.72rem',
                  fontWeight: isActive ? '700' : '600',
                  letterSpacing: '0.01em',
                  color: isActive ? activeColor : mutedColor
                }}
              >
                {t(language, item.labelKey)}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
};

export default ModernBottomNav;
